import { enumNarrow } from '../helpers/enumGuard'
import { IClientInfo } from './client'
import { IEntityInfo } from './entityInfo'
import './prospects'

export interface INewsfeedDataSheet {
  industry: string
  description: string
  recordDate: string | null
  link: string
  clientName: string
  salesforceId: string
  referralClientName: string
  referralSalesforceId: string
  wholesaler: string
  isBankruptcy: boolean
  isDistribution: boolean
  isFundraise: boolean
  isLawsuit: boolean
  isExit: boolean
  isNewFund: boolean
  amountRaised: string
  roundType: string
  isPublishing: boolean
  owner: string
  newAccountName: string
  isLeadingInvestor: boolean
}

export interface IClientNewsfeed {
  id: string
  createdAt: string
  newsfeedHeadlines: INewsfeed
  type: ClientNewsIndustries
  displayUntil: string
}

export interface INewsfeedEmails {
  id: string
  email: string
  recordDate: string
}

export interface IBrand {
  id: string
  brandName: string
  salesforceId: string
  clientInfo: IClientInfo | null
  // Latest non-archived OPS reporting for the brand's client record — replaces
  // the old brand → salesforce_prospect_info → ops_reporting hop.
  opsReportingId?: string | null
  entityInfo: IEntityInfo | null
  salesforceType: string
  brandIcon: string | null
  industry: string | null
}

export interface ILinkedAccount {
  id: string
  salesforceId: string | null
  type: NewsTags
  brandInfo: IBrand | null
}

export interface INewsfeed {
  id: string
  headline: string
  link: string
  relevanceScore: number | null
  type: NewsTags | null
  brand: string | null
  round: string | null
  amount: number | null
  industry: string | null
  isProcessed: boolean | null
  isUploaded: boolean
  createdAt: string
  updatedAt: string
  processedAt: string
  salesforceId: string | null
  isUrlTransformed: boolean
  clientNewsFeed: IClientNewsfeed[]
  clientName?: string
  clientInfo?: IClientInfo
  entityName?: string
  entityInfo?: IEntityInfo
  recordDate: string
  description: string
  originalBrand?: string
  headlineSummary: string
  newsfeedEmail: INewsfeedEmails
  brandFromSalesforce: IBrand
  isClient: boolean
  isProspect: boolean
  isEntity: boolean
  brandOptionValue?: { value: string; label: string }
  salesforceBaseUrl: string
  linkedAccounts: ILinkedAccount[]
  salesforceSyncProcessed: boolean
  clientNewsfeedHeadlines: IClientNewsfeed[]
  newsletterSection: string
  /** Direct replies on this headline — drives the row's reply badge. */
  replyCount?: number
}

export enum ClientNewsIndustries {
  Apparel = 'Apparel',
  FoodAndBeverage = 'Food & Beverage',
  Beauty = 'Beauty',
  Lifestyle = 'Lifestyle',
}

export enum NewsletterSections {
  RecentlyInDistributionNews = 'Recently in Distribution News',
  RecentlyInFundraiseNews = 'Recently in Fundraising News',
}

export interface INewsfeedData {
  data: INewsfeed[]
  totals: {
    totalItems: number
    lastRefreshed: string
  }
  clientIndustries: {
    [ClientNewsIndustries.Apparel]: IClientInfo[]
    [ClientNewsIndustries.FoodAndBeverage]: IClientInfo[]
    [ClientNewsIndustries.Beauty]: IClientInfo[]
    [ClientNewsIndustries.Lifestyle]: IClientInfo[]
  }
  appliedFilters?: {
    processedAtFrom?: string
    processedAtTo?: string
  }
}

export interface INewsfeedItemData {
  data: INewsfeed
}

export interface IBrandOptions {
  brandsOptions: { label: string; value: string }[]
}

/** What a borrower's newsfeed carries: headline display fields only, no CRM brand records. */
export type IClientNewsfeedHeadline = Pick<
  INewsfeed,
  | 'id'
  | 'headline'
  | 'originalBrand'
  | 'type'
  | 'industry'
  | 'link'
  | 'isUrlTransformed'
  | 'processedAt'
  | 'updatedAt'
>

export interface IClientNewsfeedItem extends Omit<IClientNewsfeed, 'newsfeedHeadlines'> {
  newsfeedHeadlines: IClientNewsfeedHeadline
}

export interface IClientNewsfeedData {
  data: IClientNewsfeedItem[]
}

export enum NewsTags {
  Lawsuit = 'Lawsuit',
  Fundraise = 'Fundraise',
  Other = 'Other',
  Bankruptcy = 'Bankruptcy',
  Distribution = 'Distribution',
  NewFund = 'New Fund',
  Exit = 'Exit',
  Client = 'Client',
  Prospect = 'Prospect',
  Personnel = 'Personnel',
  ABL = 'ABL',
  Industry = 'Industry',
}

export enum FundraiseRounds {
  Seed = 'Seed',
  SeriesA = 'Series A',
  SeriesB = 'Series B',
  SeriesC = 'Series C',
  SeriesD = 'Series D',
  SeriesE = 'Series E',
  Other = 'Other',
}

export const TAG_COLOR_MAPPING: Partial<
  Record<NewsTags, { backgroundColor: string; color: string }>
> = {
  [NewsTags.Lawsuit]: {
    backgroundColor: 'var(--news-tag-legal-bg)',
    color: 'var(--news-tag-legal-text)',
  },
  [NewsTags.Bankruptcy]: {
    backgroundColor: 'var(--news-tag-legal-bg)',
    color: 'var(--news-tag-legal-text)',
  },
  [NewsTags.Other]: {
    backgroundColor: 'var(--news-tag-default-bg)',
    color: 'var(--news-tag-default-text)',
  },
  [NewsTags.NewFund]: {
    backgroundColor: 'var(--news-tag-default-bg)',
    color: 'var(--news-tag-default-text)',
  },
  [NewsTags.Exit]: {
    backgroundColor: 'var(--news-tag-default-bg)',
    color: 'var(--news-tag-default-text)',
  },
  [NewsTags.Personnel]: {
    backgroundColor: 'var(--news-tag-default-bg)',
    color: 'var(--news-tag-default-text)',
  },
  [NewsTags.ABL]: {
    backgroundColor: 'var(--news-tag-default-bg)',
    color: 'var(--news-tag-default-text)',
  },
  [NewsTags.Distribution]: {
    backgroundColor: 'var(--news-tag-default-bg)',
    color: 'var(--news-tag-default-text)',
  },
  [NewsTags.Fundraise]: {
    backgroundColor: 'var(--news-tag-default-bg)',
    color: 'var(--news-tag-default-text)',
  },
  [NewsTags.Client]: {
    backgroundColor: 'var(--news-tag-relationship-bg)',
    color: 'var(--news-tag-relationship-text)',
  },
  [NewsTags.Prospect]: {
    backgroundColor: 'var(--news-tag-relationship-bg)',
    color: 'var(--news-tag-relationship-text)',
  },
  [NewsTags.Industry]: {
    backgroundColor: 'var(--news-tag-industry-bg)',
    color: 'var(--news-tag-industry-text)',
  },
}

export const TAG_OPTIONS = Object.values(NewsTags)
  .filter((item) => ![NewsTags.Client, NewsTags.Prospect, NewsTags.Industry].includes(item))
  .map((item) => {
    return { label: item, value: item }
  })

export const EMAIL_NEWSLETTER_MAPPING = {
  'expresscheckout@mail.beehiiv.com': 'Express Checkout',
  'mike@whipstitchcapital.com': 'Whipstitch Capital',
  'news@thehustle.co': 'The Hustle',
  'newsletter@divenewsletter.com': 'Dive Newsletter',
  'stitch@whipstitchcapital.com': 'Whipstitch Capital',
  'thisweekincpg@mail.beehiiv.com': 'This Week in CPG',
}
// Needs to be updated with salesforce if industries are removed or added
export enum BrandIndustries {
  Apparel = 'Apparel',
  Beauty = 'Beauty',
  Beverages = 'Beverages',
  BusinessServices = 'Business Services',
  Cannabis = 'Cannabis',
  Food = 'Food',
  Healthcare = 'Healthcare',
  Home = 'Home',
  Lifestyle = 'Lifestyle',
  Manufacturing = 'Manufacturing',
  Other = 'Other',
  Outdoor = 'Outdoor',
  PetProducts = 'Pet Products',
  Technology = 'Technology',
  ChildBaby = 'Child/Baby',
  FourWall = 'Four-wall',
}

export interface IUpdateData {
  id: string
  headline: string
  headlineSummary: string
  type: NewsTags
  link: string
  investor: string
  amount: number
  round: string
  linkedAccounts: string
  salesforceId: string
  salesforceLink: string
  otherAccountId: string
  otherAccountIds: string[]
  linkedAccountId: string
  isFundraise: boolean
  isBrand: boolean
  isOtherAccount: boolean
  industries: ClientNewsIndustries[]
  displayUntil: string
  clientNewsfeedId: string
  industry: string
}

export const NewsletterSectionsOptions = Object.values(NewsletterSections).map((item) => ({
  label: item,
  value: item,
}))

export const INDUSTRY_OPTIONS = Object.values(BrandIndustries).map((industry) => ({
  label: industry,
  value: industry,
}))

/** Cast-free narrower for NewsTags (see common/helpers/enumGuard). */
export const asNewsTag = enumNarrow(NewsTags).as
